import React from 'react';
import { useLocation, useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  IconButton,
  Button,
} from '@mui/material';
import { ArrowBack, AssignmentOutlined, VisibilityOutlined } from '@mui/icons-material';
import { EditIcon, ViewIcon } from '../../components/shared/Icons';
import { PaginationBar } from '../../components/shared/PaginationBar';
import { getPaymentDetails } from '../../components/shared/PaymentDetails';
import { isKycValue, isEsignValue } from '../../components/shared/LeadVerificationDetails';
import { ShareFormDialog } from '../../components/form-list/ShareFormDialog';
import { formPaths, publicFormUrl } from '../../components/form-list/utils';
import { FormResponsesSkeleton } from './FormResponsesSkeleton';
import { useFormResponses, RESPONSES_PAGE_SIZE } from './useFormResponses';
import { ResponsesViewToggle } from './ResponsesViewToggle';
import { ResponsesCalendar } from './calendar/ResponsesCalendar';
import './FormResponsesList.css';

const getKycDetails = (data: Record<string, any>) =>
  Object.values(data || {}).find(isKycValue);

const getEsignDetails = (data: Record<string, any>) =>
  Object.values(data || {}).find(isEsignValue);

const formatDate = (value: string) =>
  new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

const StatusBadge: React.FC<{ done: boolean; doneLabel: string }> = ({ done, doneLabel }) =>
  done ? (
    <span className="responses-status-badge responses-status-badge--done">{doneLabel}</span>
  ) : (
    <span className="responses-status-badge responses-status-badge--pending">—</span>
  );

export const FormResponsesList: React.FC = () => {
  const { tenantId, formId } = useParams<{
    tenantId: string;
    formId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backState = { backTo: `${location.pathname}${location.search}` };
  const {
    responses,
    totalCount,
    formName,
    slotFields,
    loading,
    page,
    view,
    handlePageChange,
    handleViewChange,
  } = useFormResponses(tenantId, formId);
  const [shareOpen, setShareOpen] = React.useState(false);

  const viewResponsePath = (responseId: string) =>
    `/tenant/${tenantId}/form-response/${responseId}`;

  if (loading) {
    return <FormResponsesSkeleton />;
  }

  const paths = formPaths(tenantId!);
  const hasSlotBooking = slotFields.length > 0;
  const showCalendar = hasSlotBooking && view === 'calendar';

  return (
    <Box className="admin-page responses-page">
      <Box className="responses-header">
        <IconButton className="admin-back-btn" onClick={() => navigate(`/tenant/${tenantId}/forms`)}>
          <ArrowBack sx={{ fontSize: 18 }} />
        </IconButton>
        <Box className="responses-header-text">
          <Typography className="admin-page-title">{formName || 'Untitled form'}</Typography>
        </Box>
        <Box className="responses-header-actions">
          <Button
            className="responses-header-btn"
            variant="outlined"
            startIcon={<EditIcon size={13} />}
            component={RouterLink}
            to={paths.edit(formId!)}
          >
            Edit
          </Button>
          <Button
            className="responses-header-btn"
            variant="outlined"
            startIcon={<VisibilityOutlined sx={{ fontSize: 17 }} />}
            onClick={() => setShareOpen(true)}
          >
            Preview &amp; share
          </Button>
        </Box>
      </Box>

      {hasSlotBooking && (
        <Box className="responses-toolbar">
          <ResponsesViewToggle value={view} onChange={handleViewChange} />
        </Box>
      )}

      <Box className="admin-card">
        {showCalendar ? (
          <ResponsesCalendar tenantId={tenantId!} formId={formId!} slotFields={slotFields} />
        ) : responses.length === 0 ? (
          <Box className="admin-empty">
            <Box className="admin-empty-icon">
              <AssignmentOutlined sx={{ fontSize: 30 }} />
            </Box>
            <Typography className="admin-empty-title">No responses submitted yet</Typography>
            <Typography className="admin-empty-subtitle">
              Form submissions will appear here
            </Typography>
          </Box>
        ) : (
          <TableContainer className="responses-table-scroll">
            <Table className="responses-table">
              <TableHead>
                <TableRow className="admin-table-head-row">
                  <TableCell>Submission</TableCell>
                  <TableCell>Submitted At</TableCell>
                  <TableCell>Response ID</TableCell>
                  <TableCell align="center">Payment</TableCell>
                  <TableCell align="center">KYC</TableCell>
                  <TableCell align="center">eSign (MITC)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {responses.map((response, index) => {
                  const responseId = response.id || response._id || '';
                  if (!responseId) return null;
                  return (
                    <TableRow
                      key={responseId}
                      className="admin-table-row responses-table-row"
                      component={RouterLink as any}
                      to={viewResponsePath(responseId)}
                      state={backState}
                    >
                      <TableCell>
                        <Typography className="responses-index">
                          #{(page - 1) * RESPONSES_PAGE_SIZE + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography className="responses-date">
                          {formatDate(response.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <span className="responses-id-chip">{responseId}</span>
                      </TableCell>
                      <TableCell align="center">
                        <StatusBadge done={!!getPaymentDetails(response.data)} doneLabel="Recorded" />
                      </TableCell>
                      <TableCell align="center">
                        <StatusBadge done={!!getKycDetails(response.data)} doneLabel="Verified" />
                      </TableCell>
                      <TableCell align="center">
                        <StatusBadge done={!!getEsignDetails(response.data)} doneLabel="Signed" />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          className="responses-view-btn"
                          component={RouterLink}
                          to={viewResponsePath(responseId)}
                          state={backState}
                          title="View Details"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ViewIcon size={17} color="currentColor" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!showCalendar && responses.length > 0 && (
          <PaginationBar
            page={page}
            count={Math.max(1, Math.ceil(totalCount / RESPONSES_PAGE_SIZE))}
            onChange={handlePageChange}
            disabled={totalCount <= RESPONSES_PAGE_SIZE}
          />
        )}
      </Box>

      <ShareFormDialog
        open={shareOpen}
        formName={formName}
        shareUrl={publicFormUrl(tenantId!, formId!)}
        onClose={() => setShareOpen(false)}
      />
    </Box>
  );
};
